import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, CheckCircle2, XCircle, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchKnowledgeQuestions, submitKnowledgeCheck } from "@/lib/api";
import { loadTraineeName, loadCohort } from "@/lib/progression";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const INTRO = {
  pre: {
    title: "Knowledge check: before you begin",
    body: "Ten quick questions before your first case. There is no pass or fail here; this simply captures where you are starting, so you can see how far you come by the end.",
  },
  post: {
    title: "Knowledge check: after your shift",
    body: "You have completed every case. Here are the same ten questions, freshly shuffled. Let's see how far you have come.",
  },
};

const growthLine = (pre, post) => {
  if (pre == null) return "";
  if (post > pre) return `You answered ${post - pre} more correctly than when you started. That growth is real; carry it with you into the room.`;
  if (post === pre) return "You held steady, and the depth behind those answers has grown through every case you worked.";
  return "Scores wobble; skills do not. You have practiced these exact decisions across nine cases, and that practice is what counts.";
};

const FADE_UP = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const KnowledgeResults = ({ phase, result, preScore, byId, onDone }) => (
      <div className="min-h-screen bg-stone-100 py-10 px-4">
        <motion.div {...FADE_UP} data-testid="knowledge-results" className="max-w-2xl mx-auto rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
            <GraduationCap className="h-4 w-4" /> {phase === "pre" ? "Starting point" : "Final knowledge check"}
          </div>
          <h2 data-testid="knowledge-score" className="font-heading mt-3 text-3xl font-semibold text-stone-900">
            {result.score} of {result.total}
          </h2>

          {phase === "pre" && (
            <p className="mt-2 text-base leading-relaxed text-stone-600">
              That is your starting point, not a judgment. The cases ahead will build exactly these skills, and you will take the same check again at the end of your shift.
            </p>
          )}
          {phase === "post" && preScore == null && (
            <p className="mt-2 text-base leading-relaxed text-stone-600" data-testid="knowledge-post-no-pre">
              You worked all nine cases first, so this score reflects everything the training built. Well done finishing the full shift.
            </p>
          )}
          {phase === "post" && preScore != null && (
            <div data-testid="knowledge-comparison" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Before</p>
                  <p className="font-heading text-2xl font-semibold text-stone-800 tabular-nums">{preScore}/{result.total}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-emerald-700" />
                <div className="text-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">After</p>
                  <p className="font-heading text-2xl font-semibold text-emerald-900 tabular-nums">{result.score}/{result.total}</p>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-emerald-900">{growthLine(preScore, result.score)}</p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {result.results.map((r) => {
              const question = byId[r.question_id];
              if (!question) return null;
              return (
                <div key={r.question_id} data-testid={`review-${r.question_id}`} className="rounded-xl border border-stone-200 p-4">
                  <div className="flex items-start gap-2">
                    {r.correct ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />}
                    <div>
                      <p className="text-sm font-medium text-stone-800">{question.question}</p>
                      {!r.correct && (
                        <p className="mt-1 text-sm text-stone-600">Correct answer: {r.correct_text}</p>
                      )}
                      <p className="mt-1 text-xs leading-relaxed text-stone-500">{r.explanation}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Button data-testid="knowledge-done-button" onClick={onDone} className="mt-8 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 h-11">
            {phase === "pre" ? "Start your shift" : "Back to cases"} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </motion.div>
      </div>
);

export const KnowledgeCheck = ({ phase, preScore = null, onSubmitted, onDone, onExit }) => {
  const [questions, setQuestions] = useState(null);
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchKnowledgeQuestions()
      .then((qs) => setQuestions(phase === "post" ? shuffle(qs) : qs))
      .catch(() => setError(true));
  }, [phase]);

  const q = questions?.[current];
  const progressPct = questions ? Math.round((current / questions.length) * 100) : 0;
  const byId = useMemo(() => Object.fromEntries((questions || []).map((x) => [x.id, x])), [questions]);

  const choose = async (optionId) => {
    if (busy) return;
    const next = { ...answers, [q.id]: optionId };
    setAnswers(next);
    if (current < questions.length - 1) {
      setCurrent((c) => c + 1);
      return;
    }
    setBusy(true);
    try {
      const res = await submitKnowledgeCheck({
        trainee_name: loadTraineeName(),
        cohort: loadCohort(),
        phase,
        answers: Object.entries(next).map(([question_id, option_id]) => ({ question_id, option_id })),
      });
      setResult(res);
      onSubmitted?.(res);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center text-stone-500">
        <div>
          <p>The knowledge check is unavailable right now.</p>
          <Button data-testid="knowledge-error-continue" onClick={onDone} className="mt-4 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6">Continue anyway</Button>
        </div>
      </div>
    );
  }
  if (!questions) {
    return <div className="min-h-screen grid place-items-center text-stone-400">Loading questions…</div>;
  }

  if (result) {
    return <KnowledgeResults phase={phase} result={result} preScore={preScore} byId={byId} onDone={onDone} />;
  }

  if (!started) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <motion.div {...FADE_UP} data-testid="knowledge-intro" className="w-full max-w-xl rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
            <GraduationCap className="h-4 w-4" /> Knowledge check
          </div>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-stone-900">{INTRO[phase].title}</h2>
          <p className="mt-3 text-base leading-relaxed text-stone-600">{INTRO[phase].body}</p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Button data-testid="knowledge-start-button" onClick={() => setStarted(true)} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 h-11">
              Begin <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            {onExit && (
              <button
                data-testid="knowledge-back-home"
                onClick={onExit}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-emerald-800 underline underline-offset-4 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" /> Back home
              </button>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
      <div data-testid="knowledge-question-card" className="w-full max-w-2xl rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm">
        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-stone-400">
          <span data-testid="knowledge-progress">Question {current + 1} of {questions.length}</span>
          <span>{phase === "pre" ? "Before your shift" : "After your shift"}</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>
        <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <h2 data-testid="knowledge-question" className="font-heading mt-6 text-xl sm:text-2xl font-semibold text-stone-900 leading-snug">{q.question}</h2>
          <div className="mt-6 flex flex-col gap-3">
            {q.options.map((opt, displayPos) => (
              <button
                key={opt.id}
                data-testid={`knowledge-option-${displayPos}`}
                disabled={busy}
                onClick={() => choose(opt.id)}
                className="text-left rounded-xl border border-stone-200 bg-stone-50/60 px-5 py-4 text-sm sm:text-base leading-relaxed text-stone-700 hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors duration-150 disabled:opacity-60"
              >
                {opt.text}
              </button>
            ))}
          </div>
          {busy && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Scoring…</p>
          )}
        </motion.div>
        {onExit && (
          <button
            data-testid="knowledge-back-home-question"
            onClick={onExit}
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-stone-400 hover:text-emerald-800 underline underline-offset-4 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4" /> Back home; your answers will not be saved
          </button>
        )}
      </div>
    </div>
  );
};
